import React from "react";
import CountryInput from "../CountryInput.jsx";
import AuthMessage from "./AuthMessage.jsx";
import { useLanguage } from "../../contexts/LanguageContext.jsx";
import { HiOutlineEye, HiOutlineEyeSlash } from "react-icons/hi2";

const INPUT_BASE =
  "w-full px-4 py-3 bg-slate-50/80 border border-slate-200 rounded-lg text-slate-800 placeholder-slate-400 text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 focus:bg-white";
const INPUT_SM =
  "w-full px-3 py-2.5 bg-slate-50/80 border border-slate-200 rounded-lg text-slate-800 placeholder-slate-400 text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 focus:bg-white";

const Field = ({ label, children, className = "" }) => (
  <div className={`group ${className}`}>
    {label && (
      <label className="block text-xs font-semibold text-white uppercase tracking-wider mb-1.5">
        {label}
      </label>
    )}
    <div className="relative">{children}</div>
  </div>
);

/**
 * Carte formulaire d'authentification — design aligné VisitorHome (slate + orange/ambre)
 */
const AuthFormCard = ({
  isLogin,
  formData,
  loading,
  error,
  success,
  handleChange,
  handleSubmit,
  toggleMode,
  navigate,
  onForgotPassword,
  openForgotPassword,
}) => {
  const { t } = useLanguage();
  const handleForgotPassword = onForgotPassword || openForgotPassword;
  const [showPassword, setShowPassword] = React.useState(false);

  const PasswordToggleButton = () => (
    <button
      type="button"
      onClick={() => setShowPassword((prev) => !prev)}
      className="absolute inset-y-0 right-0 px-3 flex items-center text-slate-500 hover:text-slate-700"
      aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
      title={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
    >
      {showPassword ? (
        <HiOutlineEyeSlash className="w-5 h-5" />
      ) : (
        <HiOutlineEye className="w-5 h-5" />
      )}
    </button>
  );

  return (
    <div className="animate-fade-in-right flex items-center">
      <div className="relative w-full max-w-xl mx-auto">
        {/* Carte avec bordure gauche accent */}
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-100">
          <div className="p-8 max-h-[calc(100vh-4rem)] overflow-y-auto">
            {/* En-tête minimal */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-orange-100 text-orange-600 mb-4 text-2xl">
                {isLogin ? "→" : "+"}
              </div>
              <h2 className="text-2xl font-bold text-white tracking-tight">
                {isLogin ? t("connection") : t("registration")}
              </h2>
              <p className="text-white text-sm mt-1">
                {isLogin ? t("manageAccount") : t("startFree")}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <AuthMessage type="error" message={error} />
              <AuthMessage type="success" message={success} />

              <Field label={t("email")}>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className={INPUT_BASE}
                  placeholder="exemple@email.com"
                />
              </Field>

              {!isLogin ? (
                <div className="grid grid-cols-2 gap-3 text-white">
                  <Field label={t("username")}>
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      required
                      className={INPUT_BASE}
                      placeholder="pseudo"
                    />
                  </Field>
                  <Field label={t("newPassword")}>
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      className={`${INPUT_BASE} pr-11`}
                      placeholder="••••••••"
                    />
                    <PasswordToggleButton />
                  </Field>
                </div>
              ) : (
                <>
                  <Field label={t("password")}>
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      className={`${INPUT_BASE} pr-11`}
                      placeholder="••••••••"
                    />
                    <PasswordToggleButton />
                  </Field>
                  {handleForgotPassword && (
                    <div className="flex justify-end mt-2">
                      <button
                        type="button"
                        onClick={handleForgotPassword}
                        className="text-sm font-medium text-amber-300 hover:text-white underline underline-offset-2 transition-colors"
                      >
                        {t("forgotPassword")}
                      </button>
                    </div>
                  )}
                </>
              )}

              {!isLogin && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label={t("firstName")}>
                      <input
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        className={INPUT_SM}
                        placeholder="Jean"
                      />
                    </Field>
                    <Field label={t("lastName")}>
                      <input
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        className={INPUT_SM}
                        placeholder="Dupont"
                      />
                    </Field>
                  </div>
                  <Field label={t("country")}>
                    <CountryInput
                      value={formData.country}
                      onChange={handleChange}
                      placeholder={t("selectCountry")}
                    />
                  </Field>
                  <div className="flex justify-center py-1">
                    <span className="text-xs font-medium text-orange-600 bg-amber-50 px-2.5 py-1 rounded-full">
                      {t("freeForLife")}
                    </span>
                  </div>
                </>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full mt-2 py-3.5 px-4 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 active:from-orange-700 active:to-amber-600 text-white font-semibold text-sm rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {isLogin ? t("loading") : t("loading")}
                  </span>
                ) : isLogin ? (
                  t("login")
                ) : (
                  t("register")
                )}
              </button>

              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-3 text-xs text-slate-400">
                    {t("or")}
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={toggleMode}
                className="w-full py-3 px-4 text-white hover:text-slate-800 hover:bg-amber-50/50 font-medium text-sm rounded-lg border border-slate-200 hover:border-orange-200 transition-all duration-200"
              >
                {isLogin ? t("registration") : t("haveAccount")}
              </button>
            </form>

            <div className="mt-6 pt-4 border-t border-slate-100">
              <button
                onClick={() => navigate("/")}
                className="w-full text-white hover:text-orange-400 text-sm font-medium py-2 transition-colors flex items-center justify-center gap-1.5"
              >
                <span>←</span>
                <span>{t("backToHome")}</span>
              </button>
              <div className="flex justify-center gap-4 mt-3 text-xs text-slate-400">
                <span>{t("secure")}</span>
                <span>·</span>
                <span>{t("freeForLife")}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthFormCard;
